import * as XLSX from "xlsx";
import { ValidationResult, CustomerRow, ValidationError } from "./types";

const validatePhone = (value: string): string => {
    if (!value) return "";
    const cleaned = value.replace(/[^\d+\-\n;]/g, "");
    return cleaned.split(/[\n;]/).map(part => part.trim()).filter(Boolean).join(";");
};

const validateTaxId = (value: string): string => {
    if (!value) return "";
    const match = value.match(/\d/);
    if (match) {
        return value.substring(match.index || 0);
    }
    return value;
};

const validateCreditLimit = (value: string): string => {
    if (!value) return "0";
    const cleaned = value.replace(/\D/g, "");
    return cleaned || "0";
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                // Get headers from row 3 (index 2)
                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                const headers: { [key: number]: string } = {};
                const fieldMap: { [key: string]: number } = {};
                const contactListFields: { [key: string]: number } = {};

                headerRow.forEach((header: any, colIdx: number) => {
                    if (header) {
                        const headerStr = String(header).trim();
                        headers[colIdx] = headerStr;
                        if (headerStr.startsWith("contact_list.")) {
                            const fieldName = headerStr.split(".", 2)[1];
                            contactListFields[fieldName] = colIdx;
                        } else {
                            fieldMap[headerStr] = colIdx;
                        }
                    }
                });

                const rows: CustomerRow[] = [];
                const errors: ValidationError[] = [];
                let originalRowCount = 0;

                // Process data starting from row 4 (index 3)
                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    const rowNumber = rowIdx + 1; // Excel row number (1-based)

                    // Skip empty rows
                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }
                    
                    originalRowCount++;

                    const rowErrors: ValidationError[] = [];
                    const customerData: any = {};

                    // Process regular fields
                    Object.entries(fieldMap).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }

                            // Apply field-specific validation
                            if (field === "phone") {
                                value = validatePhone(value);
                            } else if (field === "tax_id") {
                                value = validateTaxId(value);
                            } else if (field === "credit_limit") {
                                value = validateCreditLimit(value);
                            } else if (field === "category") {
                                // Category uses semicolon as separator, normalize it
                                if (value) {
                                    // Split by comma or semicolon, then join with semicolon
                                    const categories = value
                                        .split(/[,;]/)
                                        .map((cat: string) => cat.trim())
                                        .filter((cat: string) => cat.length > 0);
                                    value = categories.join(";");
                                }
                            }

                            customerData[field] = value;
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Process contact list fields
                    const contact: any = {};
                    Object.entries(contactListFields).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }

                            if (field === "phone") {
                                value = validatePhone(value);
                            }

                            contact[field] = value;
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field: `contact_list.${field}`,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Validate required fields
                    if (!customerData.name || String(customerData.name).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "name",
                            error: "Tên khách hàng là bắt buộc",
                        });
                    }

                    // Set defaults
                    if (!customerData.tier) {
                        customerData.tier = "tier 1";
                    }
                    if (!customerData.payable_days && customerData.payable_days !== 0) {
                        customerData.payable_days = 0;
                    }
                    if (!customerData.credit_limit && customerData.credit_limit !== 0) {
                        customerData.credit_limit = 0;
                    }

                    // Add contact list if present
                    if (Object.keys(contact).length > 0) {
                        // First contact is default, but we'll handle this during deduplication
                        // For now, mark as default (will be adjusted if merged)
                        contact.is_default = true;
                        customerData.contact_list = [
                            {
                                id: 1,
                                contact,
                            },
                        ];
                    } else {
                        customerData.contact_list = [
                            {
                                id: 1,
                                contact: {
                                    is_default: true,
                                    name: "",
                                    email: "",
                                    phone: "",
                                    position: "",
                                },
                            },
                        ];
                    }

                    rows.push({
                        rowNumber,
                        data: customerData,
                        isValid: rowErrors.length === 0,
                        errors: rowErrors,
                    });

                    errors.push(...rowErrors);
                }

                // Check for duplicate customers within the file
                const customerGroups: { [key: string]: CustomerRow } = {};
                const duplicateWarnings: ValidationError[] = [];

                rows.forEach((row) => {
                    const customerData = row.data;
                    // Use customer_code as primary identifier, fallback to name
                    const identifier = customerData.customer_code || customerData.name;

                    if (!identifier) {
                        return;
                    }

                    const identifierKey = String(identifier).trim().toLowerCase();

                    if (identifierKey in customerGroups) {
                        // Found duplicate - merge contact lists
                        const existingRow = customerGroups[identifierKey];
                        
                        // Add warning to the first occurrence row (the one being kept)
                        existingRow.errors.push({
                            row: existingRow.rowNumber,
                            field: customerData.customer_code ? "customer_code" : "name",
                            error: `Khách hàng trùng lặp với dòng ${row.rowNumber}. Các thông tin liên hệ sẽ được gộp lại.`,
                            data: identifier,
                        });

                        // Merge contact lists
                        const existingContacts = existingRow.data.contact_list || [];
                        const newContacts = customerData.contact_list || [];

                        // Get highest ID from existing contacts
                        let maxId = 0;
                        const existingContactDetails: string[] = [];
                        existingContacts.forEach((contactEntry: any) => {
                            if (contactEntry && contactEntry.id) {
                                maxId = Math.max(maxId, contactEntry.id);
                                if (contactEntry.contact) {
                                    existingContactDetails.push(JSON.stringify(contactEntry.contact));
                                }
                            }
                        });

                        // Add new contacts that don't already exist
                        let nextId = maxId + 1;
                        newContacts.forEach((newContactEntry: any) => {
                            if (newContactEntry && newContactEntry.contact) {
                                const contactStr = JSON.stringify(newContactEntry.contact);
                                if (!existingContactDetails.includes(contactStr)) {
                                    // New contacts should not be default (first contact in existing is already default)
                                    if (newContactEntry.contact) {
                                        newContactEntry.contact.is_default = false;
                                    }
                                    newContactEntry.id = nextId;
                                    existingContacts.push(newContactEntry);
                                    existingContactDetails.push(contactStr);
                                    nextId++;
                                }
                            }
                        });

                        // Ensure only the first contact is default
                        if (existingContacts.length > 0) {
                            existingContacts.forEach((contactEntry: any, index: number) => {
                                if (contactEntry && contactEntry.contact) {
                                    contactEntry.contact.is_default = index === 0;
                                }
                            });
                        }

                        existingRow.data.contact_list = existingContacts;
                    } else {
                        customerGroups[identifierKey] = row;
                    }
                });

                // Update rows to use deduplicated data
                const uniqueRows = Object.values(customerGroups);

                resolve({ rows: uniqueRows, errors, originalRowCount });
            } catch (e: any) {
                reject(new Error(`Lỗi đọc file: ${e.message}`));
            }
        };

        reader.onerror = () => {
            reject(new Error("Không thể đọc file"));
        };

        reader.readAsArrayBuffer(file);
    });
};

